import { Box, Button } from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  buttonColor?: string;
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '2xs';
  expandText?: string;
  collapseText?: string;
  children?: React.ReactNode;
}

export function ExpandableText({
  text,
  maxLines = 2,
  fontSize = 'sm',
  fontWeight = 'medium',
  color = 'fg',
  buttonColor = 'fg.subtle',
  buttonSize = 'xs',
  expandText: expandTextProp,
  collapseText: collapseTextProp,
  children,
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const expandText = expandTextProp ?? t('action.more');
  const collapseText = collapseTextProp ?? t('action.collapse');

  useEffect(() => {
    if (textRef.current && text) {
      const element = textRef.current;
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * maxLines;
      setShowButton(element.scrollHeight > maxHeight);
    } else {
      setShowButton(false);
    }
  }, [text, maxLines]);

  if (!text) {
    return <>{children}</>;
  }

  return (
    <Box position="relative">
      {isExpanded ? (
        <Box
          ref={textRef}
          fontSize={fontSize}
          fontWeight={fontWeight}
          color={color}
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {text}
          {showButton && (
            <Button
              as="span"
              variant="ghost"
              size={buttonSize}
              color={buttonColor}
              p="0"
              h="auto"
              minH="auto"
              fontSize={buttonSize}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              ml="1"
              bg="transparent"
              _hover={{ bg: 'bg.muted' }}
              cursor="pointer"
            >
              {collapseText}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box
            ref={textRef}
            fontSize={fontSize}
            fontWeight={fontWeight}
            color={color}
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            lineClamp={maxLines}
            pr={showButton ? '8' : '0'}
          >
            {text}
          </Box>
          {showButton && (
            <Button
              variant="ghost"
              size={buttonSize}
              color={buttonColor}
              p="0"
              h="auto"
              minH="auto"
              fontSize={buttonSize}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              position="absolute"
              bottom="0"
              right="0"
              bg="transparent"
              _hover={{ bg: 'bg.subtle' }}
            >
              {expandText}
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
